import {SyntheticEvent} from 'react';
import {useField, useFormikContext} from 'formik';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {FormikAutocompleteProps} from "@/types/form";
import InputErrorMessage from "@/components/utilities/input-error-message";
import Box from "@mui/material/Box";
import {replaceInvalidCharacters} from "@/utils/replace-invalid-characters";

type OptionType = Record<string, any>;

function isHtml(content: unknown): content is string {
    const htmlRegex = /<\/?[a-z][\s\S]*>/i;
    return typeof content === 'string' && htmlRegex.test(content);
}

function stripHtmlTags(content: string): string {
    return content.replace(/<\/?[^>]+(>|$)/g, '');
}


export function RHFAutocomplete<TOption extends OptionType>({
                                                                name,
                                                                label,
                                                                helperText,
                                                                placeholder = 'انتخاب کنید',
                                                                options = [],
                                                                valueKey = 'value',
                                                                labelKey = 'label',
                                                                returnValue = 'value',
                                                                onChangeCallback,
                                                                getOptionLabel,
                                                                ...other
                                                            }: FormikAutocompleteProps<TOption>) {
    const [field, meta] = useField(name);

    const {setFieldValue, setFieldTouched} = useFormikContext<any>();

    const currentValue = field.value;
    const selectedOption =
        returnValue === 'labelValue' && currentValue && typeof currentValue === 'object'
            ? (options.find((o: any) => o?.[valueKey] === currentValue?.value) as TOption | undefined) ?? null
            : (options.find((o: any) => o?.[valueKey] === currentValue) as TOption | undefined) ?? null;

    return (
        <Box width="100%">
            <Autocomplete
                fullWidth
                noOptionsText="گزینه‌ای موجود نیست"
                loadingText="در حال بارگذاری..."
                clearText="پاک کردن"
                closeText="بستن"
                openText="باز کردن"

                options={options.filter(Boolean) as TOption[]}

                filterOptions={(options, state) => {
                    const search = replaceInvalidCharacters(state.inputValue)
                        .trim()
                        .toLowerCase();

                    if (!search) return options;

                    return options.filter((option: any) => {
                        const rawLabel = option?.[labelKey];

                        const label = replaceInvalidCharacters(
                            rawLabel
                                ? isHtml(rawLabel)
                                    ? stripHtmlTags(rawLabel)
                                    : String(rawLabel)
                                : ""
                        )
                            .trim()
                            .toLowerCase();

                        return label.includes(search);
                    });
                }}

                value={selectedOption}

                isOptionEqualToValue={(option: any, value: any) =>
                    option?.[valueKey] === value?.[valueKey]
                }

                getOptionLabel={
                    getOptionLabel ??
                    ((option: any) => {
                        const lbl = option?.[labelKey];

                        if (!lbl) return "";

                        return isHtml(lbl)
                            ? stripHtmlTags(lbl)
                            : String(lbl);
                    })
                }

                renderOption={(props, option: any) => {
                    const lbl = option?.[labelKey];

                    return (
                        <li {...props}>
                            {lbl
                                ? isHtml(lbl)
                                    ? <span dangerouslySetInnerHTML={{ __html: lbl }} />
                                    : String(lbl)
                                : "بدون نتیجه"}
                        </li>
                    );
                }}

                onKeyDown={(event) => {
                    if (event.key !== 'Delete') return;

                    event.preventDefault();

                    const emptyValue =
                        returnValue === 'labelValue'
                            ? null
                            : '';

                    setFieldValue(name, emptyValue);
                    setFieldTouched(name, true, false);

                    onChangeCallback?.(emptyValue);
                }}

                onChange={(_e: SyntheticEvent, newValue: TOption | null) => {
                    let result: any = newValue;

                    if (returnValue === "value") {
                        result = newValue ? (newValue as any)[valueKey] : "";
                    } else if (returnValue === "labelValue") {
                        result = newValue
                            ? {
                                label: (newValue as any)[labelKey],
                                value: (newValue as any)[valueKey],
                            }
                            : null;
                    }

                    setFieldValue(name, result);
                    setFieldTouched(name, true, false);
                    onChangeCallback?.(result);
                }}

                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        placeholder={placeholder}
                        error={Boolean(meta.touched && meta.error)}
                        helperText={helperText}
                        inputProps={{
                            ...params.inputProps,
                            autoComplete: "off",
                            style: {
                                paddingLeft: 8,
                                paddingRight: 8,
                                paddingTop: 5,
                                paddingBottom: 5,
                            },
                        }}
                    />
                )}

                {...other}
            />
            <InputErrorMessage
                error={Boolean(meta.touched && meta.error)}
                errorMessage={(meta.touched && meta.error) ? String(meta.error) : ""}
            />
        </Box>
    );
}
